import { useRef } from 'react';
import { useReactToPrint } from "react-to-print";
import { Col, Modal, Row } from 'react-bootstrap';
import logo from "../../icons/godrej-logo-dark.svg";
import './ComparisonSheetPDF.css';
import PropTypes from 'prop-types';
import { formatDate, formatDateInDDMMYYYY } from '../../functions/common-functions';

const ComparisonSheetPDFModal = ({ show, onHide, enquiryData, comparisonRowData, enquiryRemarks }) => {
    const formatBidDateTime = (input, { useUTC = false, strict = true } = {}) => {
        if (input == null || input === "") return "-";

        const parseDDMMYYYY_HHmm = (str) => {
            if (typeof str !== "string") return null;

            const [datePart, timePart = ""] = str.trim().split(/\s+/);
            if (!datePart) return null;

            const [ddStr, mmStr, yyyyStr] = datePart.split("/");
            if (!ddStr || !mmStr || !yyyyStr) return null;

            const dd = Number(ddStr);
            const mm = Number(mmStr);
            const yyyy = Number(yyyyStr);

            let HH = 0, MM = 0;
            if (timePart) {
                const [HHStr, MMStr = "0"] = timePart.split(":");
                HH = Number(HHStr);
                MM = Number(MMStr);
            }

            // Basic range checks
            const basicValid =
                Number.isInteger(dd) && Number.isInteger(mm) && Number.isInteger(yyyy) &&
                Number.isInteger(HH) && Number.isInteger(MM) &&
                mm >= 1 && mm <= 12 &&
                dd >= 1 && dd <= 31 &&
                HH >= 0 && HH <= 23 &&
                MM >= 0 && MM <= 59;

            if (!basicValid) return null;

            // Build date (local or UTC)
            const date = useUTC
                ? new Date(Date.UTC(yyyy, mm - 1, dd, HH, MM, 0, 0))
                : new Date(yyyy, mm - 1, dd, HH, MM, 0, 0);

            if (Number.isNaN(date.getTime())) return null;

            // Strict validation: ensure constructed date matches components (e.g., reject 31/02)
            if (strict) {
                const d = useUTC ? date.getUTCDate() : date.getDate();
                const m = (useUTC ? date.getUTCMonth() : date.getMonth()) + 1;
                const y = useUTC ? date.getUTCFullYear() : date.getFullYear();
                const h = useUTC ? date.getUTCHours() : date.getHours();
                const min = useUTC ? date.getUTCMinutes() : date.getMinutes();
                if (d !== dd || m !== mm || y !== yyyy || h !== HH || min !== MM) {
                    return null;
                }
            }

            return date;
        };

        // Normalize input to Date
        let dateObj = null;

        if (input instanceof Date) {
            dateObj = input;
        } else if (typeof input === "number") {
            // timestamp in ms
            dateObj = new Date(input);
        } else if (typeof input === "string") {
            // 1) Try DD/MM/YYYY HH:mm
            dateObj = parseDDMMYYYY_HHmm(input);

            // 2) Fallback: native parse (ISO or recognized formats)
            if (!dateObj) {
                const t = Date.parse(input);
                if (!Number.isNaN(t)) dateObj = new Date(t);
            }
        }

        if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "-";

        // Format using en-GB with capital AM/PM
        const opts = {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
            ...(useUTC ? { timeZone: "UTC" } : {}),
        };

        const formatted = dateObj.toLocaleString("en-GB", opts);
        // Convert am/pm to AM/PM
        return formatted.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
    };

    const fileName = `ComparisonSheet_${enquiryData?.EnquiryNumber}`;

    const componentRef = useRef();
    
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: fileName
    });

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                size="xl"
                backdrop="static"
                className="pdf-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter" className="modal-title">Comparison Sheet PDF Report</Modal.Title>
                    <button onClick={handlePrint} className="print-button btn-color btn btn-primary">Print</button>
                </Modal.Header>
                <Modal.Body>
                    <div ref={componentRef} className="pdf-content">
                        <Row>
                            <Col xs={2}>
                                <img src={logo} alt="logo" className="pdf-logo" />
                            </Col>
                            <Col xs={10}>
                                <div className="title-text">
                                    <span className="pdf-header-title">Godrej Enterprises Group</span>
                                    <span className="pdf-header-title-small">Godrej & Boyce Mfg. Co. Ltd</span>
                                </div>
                            </Col>
                        </Row>
                        <hr />
                        <p className="center-align">
                            <div className="form-title-text">COMPARISON SHEET</div>
                            <div className="form-subtitle">Scrap Tender Management System</div>
                        </p>

                        <hr />
                        <div className="form-data">
                            <div>
                                <div className="data-row mb-3">
                                    <div className="data-item">
                                        <span className="pdf-data-label">Enquiry Number:</span>
                                        <span className="pdf-data-value">{enquiryData?.EnquiryNumber}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enquiry Details Table */}
                        <div className='form4-data-label'>Enquiry Details</div>
                        <table className='pdf-table'>
                            <tbody>
                                <tr>
                                    <td><strong>Enquiry Number</strong></td>
                                    <td>{enquiryData?.EnquiryNumber}</td>
                                    <td><strong>Enquiry Date</strong></td>
                                    <td>{formatDate(enquiryData?.InsertedOn)}</td>
                                    <td><strong>Due Date</strong></td>
                                    <td>{formatDateInDDMMYYYY(enquiryData?.DueDate)}</td>
                                </tr>
                                <tr>
                                    <td><strong>Item Code</strong></td>
                                    <td>{enquiryData?.ItemCode}</td>
                                    <td><strong>Item Description</strong></td>
                                    <td>{enquiryData?.ItemDescription}</td>
                                    <td><strong>Item Details</strong></td>
                                    <td>{enquiryData?.ItemDetail}</td>
                                </tr>
                                <tr>
                                    <td><strong>Item Type</strong></td>
                                    <td>{enquiryData?.ItemType}</td>
                                    <td><strong>Quantity</strong></td>
                                    <td>{enquiryData?.Quantity}</td>
                                    <td><strong>UOM</strong></td>
                                    <td>{enquiryData?.UOM}</td>
                                </tr>
                                <tr>
                                    <td><strong>GST%</strong></td>
                                    <td>{enquiryData?.GST}</td>
                                    <td><strong>Product Class</strong></td>
                                    <td>{enquiryData?.EnquirySource === "Disposal" ? enquiryData?.ProductClass || "-" : "-"}</td>
                                    <td><strong>Commodity Code</strong></td>
                                    <td>{enquiryData?.EnquirySource === "Disposal" ? enquiryData?.CommodityCode || "-" : "-"}</td>
                                </tr>
                                <tr>
                                    <td><strong>Location</strong></td>
                                    <td>{enquiryData?.Location}</td>
                                    <td><strong>Division</strong></td>
                                    <td>{enquiryData?.Division}</td>
                                    <td><strong>Disposal Intimation Number</strong></td>
                                    <td>{enquiryData?.EnquirySource === "Disposal" ? enquiryData?.EnquirySourceNumber || "-" : "-"}</td>
                                </tr>
                                <tr>
                                    <td><strong>Logistic Company</strong></td>
                                    <td>{enquiryData?.LogisticCompany}</td>
                                    <td><strong>SO Number</strong></td>
                                    <td>{enquiryData?.SONumber}</td>
                                    <td></td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                        <br />

                        <hr />
                        <div className='form4-data-label'>Comparison Sheet</div>
                        <table className='pdf-table'>
                            <thead>
                                <tr>
                                    <th>BP Code</th>
                                    <th>BP Name</th>
                                    <th>Price ₹ (Excl. taxes)</th>
                                    <th>Bid Submission Date Time</th>
                                    <th>Revised Due Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonRowData?.length
                                    ? comparisonRowData.map((row, index) => (
                                        <tr 
                                            key={index} 
                                            className={index === 0 && parseFloat(row.CurrentBid) > 0 ? 'highest-bidder' : ''}
                                        >
                                            <td>{row.CustomerCode}</td>
                                            <td>{row.CustomerUserName}</td>
                                            <td>{row.CurrentBid || "No Bid Placed"}</td>
                                            <td>{formatBidDateTime(row.CurrentBidDate)}</td>
                                            <td>{row.RevisedDueDate || "-"}</td>
                                            <td>
                                                {row.WithdrawStatus === "Withdrawn" 
                                                    ? `Withdrawn: ${row.WithdrawReason}` 
                                                    : "Active"
                                                }
                                            </td>
                                        </tr>
                                    ))
                                    : (
                                        <tr>
                                            <td colSpan="6" style={{textAlign: 'center', fontStyle: 'italic', color: '#666'}}>No records</td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                        <br />

                        {/* Enquiry Remarks */}
                        {enquiryRemarks && (
                            <>
                                <hr />
                                <div className="form4-data-label">Enquiry Remarks</div>
                                <div className="pdf-remarks-box">
                                    <p style={{margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px'}}>
                                        {enquiryRemarks}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

ComparisonSheetPDFModal.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    enquiryData: PropTypes.object,
    comparisonRowData: PropTypes.array,
    enquiryRemarks: PropTypes.string
};

export default ComparisonSheetPDFModal;